import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { InputAdornment, MenuItem, FormControl } from "@mui/material";
import {
	useEditWalletMutation,
	useWalletCategoriesQuery,
	useWalletsQuery,
} from "../../../../../services/queryHooks";
import { Loading } from "../../../../../components/common/Loading";
import { useTranslation } from "react-i18next";
import { returnCategoryTranslation } from "../Functions/returnCategoryTranslation";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getOldestTransactionDate } from "../../../../../services/api";
import { validateTextField } from "../../../../../utils/validation";

export default function EditWalletModal({ isOpen, onClose, type, wallet }) {
	const [name, setName] = useState(wallet.name);
	const [category, setCategory] = useState(wallet.category);
	const [balance, setBalance] = useState(wallet.balance);
	const [createdAt, setCreatedAt] = useState(dayjs(wallet.createdAt));
	const [oldestDate, setOldestDate] = useState(null);
	const [errorName, setErrorName] = useState("");
	const [errorCategory, setErrorCategory] = useState("");
	const [errorBalance, setErrorBalance] = useState("");
	const [errorCreatedAt, setErrorCreatedAt] = useState("");
	const { mutate, isError, error } = useEditWalletMutation();
	const { data: walletCategories, isPending: walletCategoriesLoading } =
		useWalletCategoriesQuery();
	const { data: wallets, isPending: walletsLoading } = useWalletsQuery();
	const { t } = useTranslation();
	const userLanguage = localStorage.getItem("i18nextLng");

	useEffect(() => {
		const fetchOldestDate = async () => {
			try {
				const data = await getOldestTransactionDate(wallet._id);
				setOldestDate(data?.oldestTransactionDate || null);
			} catch (error) {
				console.error(t("oldestTransactionError"), error);
			}
		};
		if (wallet._id) {
			fetchOldestDate();
		}
	}, [wallet, t]);

	const handleBalanceChange = (e) => {
		let value = e.target.value;
		value = value.replace(/[^0-9.,-]/g, "");
		value = value.replace(",", ".");
		if (value.indexOf("-") > 0) {
			value = value.replace("-", "");
		}
		if (value.includes("-")) {
			value = "-" + value.replace(/-/g, "");
		}
		const regex = /^-?\d{0,12}(\.\d{0,2})?$/;
		if (!regex.test(value)) return;
		setBalance(value);
	};
	const handleCloseDialog = () => {
		onClose();
		setBalance("");
		setName("");
		setErrorName("");
		setErrorBalance("");
		setErrorCategory("");
		setErrorCreatedAt("");
	};

	const minDate = dayjs("2000-01-01");
	const maxDate = dayjs();

	const validateInputs = () => {
		let isValid = true;
		setErrorName("");
		setErrorBalance("");
		setErrorCategory("");
		setErrorCreatedAt("");
		const nameError = validateTextField(name, t, {
			fieldName: t("walletName"),
		});
		if (nameError) {
			setErrorName(nameError);
			isValid = false;
		} else if (
			wallets.some(
				(w) =>
					w._id !== wallet._id &&
					w.name.toLowerCase() === name.toLowerCase() &&
					w.type === type
			)
		) {
			setErrorName(t("walletNameExistsError"));
			isValid = false;
		}
		if (!balance && balance !== 0) {
			isValid = false;
			setErrorBalance(t("walletBalanceError"));
		}
		if ((type === "asset" || type === "liability") && !category) {
			isValid = false;
			setErrorCategory(t("walletCategoryError"));
		}
		if (type === "liability" && balance > 0) {
			isValid = false;
			setErrorBalance(t("liabilityBalanceError"));
			return isValid;
		}
		if (type === "asset" && balance < 0) {
			isValid = false;
			setErrorBalance(t("assetBalanceError"));
			return isValid;
		}
		if (createdAt.isAfter(dayjs(oldestDate))) {
			isValid = false;
			setErrorCreatedAt(
				t("walletCreationDateError", {
					oldestDate: dayjs(oldestDate).format("DD.MM.YYYY"),
				})
			);
		} else if (createdAt.isBefore(minDate) || createdAt.isAfter(maxDate)) {
			isValid = false;
			setErrorCreatedAt(t("dateOutOfRange"));
		} else if (!createdAt || !createdAt.isValid()) {
			isValid = false;
			setErrorCreatedAt(t("invalidDate"));
		}
	};
	const handleSubmit = (e) => {
		e.preventDefault();
		if (errorName || errorBalance || errorCategory || errorCreatedAt) {
			return;
		}

		const nothingChanged =
			name === wallet.name &&
			category === wallet.category &&
			Number(balance) === Number(wallet.balance) &&
			dayjs(createdAt).isSame(dayjs(wallet.createdAt), "day");

		if (nothingChanged) {
			onClose();
			return;
		}

		mutate({
			walletId: wallet._id,
			walletName: name,
			walletCategory: category,
			balance: Number(balance),
			createdAt: createdAt.toISOString(),
		});
		onClose();
	};
	if (walletCategoriesLoading || walletsLoading) {
		return <Loading />;
	}
	const dialogTitle = () => {
		switch (type) {
			case "cash":
				return t("editCashWallet");
			case "asset":
				return t("editAssetWallet");
			case "liability":
				return t("editLiabilityWallet");
			default:
				return t("editWallet");
		}
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleCloseDialog}
				disableEnforceFocus
				maxWidth="xs"
			>
				<DialogTitle>{dialogTitle()}</DialogTitle>
				<form noValidate onSubmit={handleSubmit}>
					<DialogContent>
						<TextField
							autoFocus
							type="text"
							label={t("name")}
							fullWidth
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={errorName !== ""}
							helperText={errorName}
						/>
						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={userLanguage}
							>
								<DatePicker
									slotProps={{
										textField: {
											size: "small",
											error: errorCreatedAt !== "",
											helperText: errorCreatedAt,
										},
									}}
									value={createdAt}
									label={t("walletCretionDate")}
									onChange={(newValue) => {
										setCreatedAt(newValue);
									}}
									minDate={minDate}
									maxDate={maxDate}
								/>
							</LocalizationProvider>
						</FormControl>
						{(type === "asset" || type === "liability") && (
							<>
								<TextField
									sx={{ mt: 2 }}
									select
									label={
										type === "asset"
											? t("assetCategory")
											: t("liabilityCategory")
									}
									fullWidth
									error={errorCategory !== ""}
									helperText={errorCategory}
									value={category}
									onChange={(e) =>
										setCategory(e.target.value)
									}
								>
									{walletCategories[type].map((category) => (
										<MenuItem
											key={category}
											value={category}
										>
											{returnCategoryTranslation(
												t,
												category
											)}
										</MenuItem>
									))}
								</TextField>
								<TextField
									label={t("balance")}
									fullWidth
									inputProps={{ inputMode: "decimal" }}
									value={balance}
									onChange={handleBalanceChange}
									error={errorBalance !== ""}
									helperText={errorBalance}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												{wallet.currency}
											</InputAdornment>
										),
									}}
									sx={{ mt: 2 }}
								/>
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDialog}>
							{t("cancel")}
						</Button>
						<Button
							type="submit"
							variant="contained"
							onClick={validateInputs}
						>
							{t("save")}
						</Button>
						{isError && <p>Błąd: {error.message}</p>}
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
